import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import api from '../api/axios';

interface InviteInfo {
  email: string;
  first_name: string;
  last_name: string;
  account_name: string;
}

export default function AcceptInvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [inviteInfo, setInviteInfo] = useState<InviteInfo | null>(null);
  const [validationError, setValidationError] = useState('');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) return;
    api.get(`/invitations/validate/${token}/`)
      .then(r => {
        setInviteInfo(r.data);
        setFirstName(r.data.first_name || '');
        setLastName(r.data.last_name || '');
      })
      .catch(err => {
        setValidationError(err.response?.data?.error || 'Invalid invitation link.');
      })
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/invitations/accept/', {
        token,
        password,
        first_name: firstName,
        last_name: lastName,
      });
      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to accept invitation.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#F8FAFC' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (validationError) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#F8FAFC' }}>
        <Card variant="outlined" sx={{ maxWidth: 420, width: '100%', mx: 2, borderColor: '#D7E1E8' }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
              <span style={{ color: '#102B3D' }}>Full</span>
              <span style={{ color: '#10A4C2' }}>Financials</span>
            </Typography>
            <Alert severity="error">{validationError}</Alert>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (success) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#F8FAFC' }}>
        <Card variant="outlined" sx={{ maxWidth: 420, width: '100%', mx: 2, borderColor: '#D7E1E8' }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
              <span style={{ color: '#102B3D' }}>Full</span>
              <span style={{ color: '#10A4C2' }}>Financials</span>
            </Typography>
            <Alert severity="success" sx={{ mb: 3 }}>Your account has been created!</Alert>
            <Button variant="contained" fullWidth onClick={() => navigate('/login')}>
              Sign In
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#F8FAFC' }}>
      <Card variant="outlined" sx={{ maxWidth: 420, width: '100%', mx: 2, borderColor: '#D7E1E8' }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
              <span style={{ color: '#102B3D' }}>Full</span>
              <span style={{ color: '#10A4C2' }}>Financials</span>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Set up your account for <strong>{inviteInfo?.account_name}</strong>
            </Typography>
          </Box>

          <Alert severity="info" sx={{ mb: 3 }}>
            Signing up as <strong>{inviteInfo?.email}</strong>
          </Alert>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <form onSubmit={handleSubmit}>
            <TextField
              label="First Name"
              fullWidth
              value={firstName}
              onChange={e => setFirstName(e.target.value)}
              sx={{ mb: 2 }}
              autoFocus
            />
            <TextField
              label="Last Name"
              fullWidth
              value={lastName}
              onChange={e => setLastName(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Password"
              type="password"
              fullWidth
              value={password}
              onChange={e => setPassword(e.target.value)}
              sx={{ mb: 2 }}
              autoComplete="new-password"
              helperText="At least 8 characters"
            />
            <TextField
              label="Confirm Password"
              type="password"
              fullWidth
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              sx={{ mb: 3 }}
              autoComplete="new-password"
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={submitting || !password || !confirmPassword}
              sx={{ py: 1.3 }}
            >
              {submitting ? <CircularProgress size={24} color="inherit" /> : 'Create Account'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}
